import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { wrappedCount } from './wrap.js';

/**
 * How many children of a wrapping box fit inside `lines` of it. Attach the
 * returned ref to the box; render everything while the count is null, then the
 * count itself once it settles.
 *
 * The null is the measuring pass, and it has to exist: the count can only be
 * read off a box holding *every* item, so the caller renders the full list, the
 * layout effect measures it, and the second render clamps. That pass never
 * reaches the screen — layout effects run before paint.
 *
 * The box has to be laid out at its container's width, never shrink-to-fit:
 * `clientWidth` is the whole measurement, so a box that sizes to its items feeds
 * back on itself — clamping drops items, the box narrows, the observer fires, and
 * the count oscillates.
 *
 * `resetKey` identifies the item set. Here the items arrive from the server and
 * change as the filter narrows, so a measure taken against the previous set (or
 * against none at all, on the first paint before the facets land) has to be
 * thrown away rather than clamped with.
 */
export function useVisibleCount<T extends HTMLElement>(lines: number, resetKey: string) {
  const ref = useRef<T>(null);
  const [count, setCount] = useState<number | null>(null);
  const measuredAt = useRef(0);

  // Declared before the measuring pass so it runs first in the same commit: it
  // clears the count, and the re-render that follows is what measures.
  useLayoutEffect(() => {
    setCount(null);
  }, [resetKey]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || count !== null) return;
    const items = [...el.children].map((node) => node.getBoundingClientRect().width);
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
    measuredAt.current = el.clientWidth;
    setCount(wrappedCount(items, el.clientWidth, gap, lines));
  }, [count, lines]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Only a *width* change can change where the wrap falls, but the observer
    // fires on height too — and on its own first observation. Both would remeasure
    // a box whose width nobody touched: once on mount, discarding the count just
    // taken, and again on every expand or collapse, since that is exactly what
    // changes the box's height.
    const observer = new ResizeObserver(() => {
      if (!ref.current || ref.current.clientWidth === measuredAt.current) return;
      setCount(null);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, count] as const;
}
